/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import Visualization from '../visualization';
import PassthroughTransformation from '../../tabledata/passthrough';

import {
  getTableColumnType,
  initializeTableConfig,
  isBtnGroupWidget,
  isCheckboxWidget,
  isInputWidget,
  isOptionWidget,
  isTextareaWidget,
  resetTableOptionConfig,
  toNumber,
  TableColumnType,
  updateColumnTypeState,
} from './visualization-util';

const SETTING_TEMPLATE = require('./visualization-table-setting.html');

const TABLE_OPTION_SPECS = [];

/**
 * Visualize data in table format
 */
export default class TableVisualization extends Visualization {
  constructor(targetEl, config) {
    super(targetEl, config);
    this.passthrough = new PassthroughTransformation(config);
    this.emitTimeout = null;
    this.isRestoring = false;

    initializeTableConfig(config, TABLE_OPTION_SPECS);
  }

  getColumnMinWidth(colName) {
    let width = 150; // default
    const calculatedWidth = colName.length * 10;

    // use the broad one
    if (calculatedWidth > width) {
      width = calculatedWidth;
    }

    return width;
  }

  getSortedValue(a, b) {
    return a > b ? 1 : a === b ? 0 : -1;
  }

  isSortableNumeric(v) {
    // eslint-disable-next-line valid-typeof
    return typeof v === 'bigint' || !isNaN(v);
  }

  getNumericSortedValue(a, b) {
    if (!this.isSortableNumeric(a) && !this.isSortableNumeric(b)) {
      return 0;
    } else if (!this.isSortableNumeric(a)) {
      return 1;
    } else if (!this.isSortableNumeric(b)) {
      return -1;
    } else {
      return this.getSortedValue(a, b);
    }
  }

  createGridOptions(tableData, onRegisterApiCallback, config) {
    const rows = tableData.rows;

    const gridData = rows.map((r) => {
      return tableData.columns.reduce((acc, col, index) => {
        acc[`${index}`] = r[index];
        return acc;
      }, {});
    });

    return {
      data: gridData,
      enableGridMenu: false,
      modifierKeysToMultiSelectCells: true,
      exporterMenuCsv: true,
      exporterMenuPdf: false,
      flatEntityAccess: true,
      fastWatch: false,
      treeRowHeaderAlwaysVisible: false,
      exporterExcelFilename: 'myFile.xlsx',

      columnDefs: tableData.columns.map((c, i) => {
        const self = this;
        return {
          displayName: c.name,
          name: `${i}`,
          type: getTableColumnType(c.type),
          enableHiding: false,
          cellTemplate: `
            <div ng-if="!grid.getCellValue(row, col).startsWith('%html')"
                 class="ui-grid-cell-contents">
              {{grid.getCellValue(row, col)}}
            </div>
            <div ng-if="grid.getCellValue(row, col).startsWith('%html')"
                 ng-bind-html="grid.getCellValue(row, col).split('%html')[1]"
                 class="ui-grid-cell-contents">
            </div>
          `,
          minWidth: this.getColumnMinWidth(c.name),
          width: '*',
          sortingAlgorithm: function(a, b, row1, row2, sortType, gridCol) {
            const colType = gridCol.colDef.type;
            if (colType === TableColumnType.NUMBER || colType === TableColumnType.NUMBER_STR) {
              return self.getNumericSortedValue(toNumber(a), toNumber(b));
            } else if (colType === TableColumnType.STRING) {
              return self.getSortedValue(a.toString(), b.toString());
            } else if (colType === TableColumnType.DATE) {
              return self.getSortedValue(new Date(a), new Date(b));
            } else {
              return self.getSortedValue(a, b);
            }
          },
        };
      }),
      rowEditWaitInterval: -1, /** disable saveRow event */
      enableRowHashing: true,
      saveFocus: false,
      saveScroll: false,
      saveSort: true,
      savePinning: true,
      saveGrouping: true,
      saveGroupingExpandedStates: true,
      saveOrder: true, // column order
      saveVisible: true, // column visibility
      saveTreeView: true,
      saveFilter: true,
      saveSelection: false,
    };
  }

  getGridElemId() {
    // angular doesn't allow `-` in scope variable name
    const gridElemId = `${this.targetEl[0].id}_grid`.replace('-', '_');
    return gridElemId;
  }

  getGridApiId() {
    // angular doesn't allow `-` in scope variable name
    const gridApiId = `${this.targetEl[0].id}_gridApi`.replace('-', '_');
    return gridApiId;
  }

  refresh() {
    const gridElemId = this.getGridElemId();
    const gridElem = angular.element(`#${gridElemId}`);

    if (gridElem) {
      gridElem.css('height', this.targetEl.height() - 10);
      const gridApiId = this.getGridApiId();
      const scope = this.getScope();
      if (scope !== undefined && scope[gridApiId] !== undefined) {
        scope[gridApiId].core.handleWindowResize();
      }
    }
  }

  refreshGrid() {
    const gridElemId = this.getGridElemId();
    const gridElem = angular.element(`#${gridElemId}`);

    if (gridElem) {
      const scope = this.getScope();
      const gridApiId = this.getGridApiId();
      scope[gridApiId].core.notifyDataChange(this._uiGridConstants.dataChange.ALL);
    }
  }

  updateColDefType(colDef, type) {
    if (type === colDef.type) {
      return;
    }

    colDef.type = type;
    const colName = colDef.name;
    const config = this.config;
    if (config.tableColumnTypeState.names && config.tableColumnTypeState.names[colName]) {
      config.tableColumnTypeState.names[colName] = type;
      this.persistConfigWithGridState(this.config);
    }
  }

  addColumnMenus(gridOptions) {
    if (!gridOptions || !gridOptions.columnDefs) {
      return;
    }

    const self = this; // for closure

    // SHOULD use `function() { ... }` syntax for each action to get `this`
    gridOptions.columnDefs.map((colDef) => {
      colDef.menuItems = [
        {
          title: 'Type: String',
          action: function() {
            self.updateColDefType(this.context.col.colDef, TableColumnType.STRING);
          },
          active: function() {
            return this.context.col.colDef.type === TableColumnType.STRING;
          },
        },
        {
          title: 'Type: Number',
          action: function() {
            self.updateColDefType(this.context.col.colDef, TableColumnType.NUMBER_STR);
          },
          active: function() {
            const colType = this.context.col.colDef.type;
            return colType === TableColumnType.NUMBER_STR || colType === TableColumnType.NUMBER;
          },
        },
        {
          title: 'Type: Date',
          action: function() {
            self.updateColDefType(this.context.col.colDef, TableColumnType.DATE);
          },
          active: function() {
            return this.context.col.colDef.type === TableColumnType.DATE;
          },
        },
      ];
    });
  }

  setDynamicGridOptions(gridOptions) {
    gridOptions.showGridFooter = false;
    gridOptions.showColumnFooter = false;
    gridOptions.enableFiltering = false;

    gridOptions.enablePagination = false;
    gridOptions.enablePaginationControls = false;

    // selection can't be rendered dynamically in ui-grid 4.0.4
    gridOptions.enableRowSelection = false;
    gridOptions.enableRowHeaderSelection = false;
    gridOptions.enableFullRowSelection = false;
    gridOptions.enableSelectAll = false;
    gridOptions.enableGroupHeaderSelection = false;
    gridOptions.enableSelectionBatchEvent = false;
  }

  append(row, columns) {
    const gridOptions = this.getGridOptions();
    this.setDynamicGridOptions(gridOptions, this.config);
    // this.refreshGrid()
    const gridElemId = this.getGridElemId();
    const gridElem = angular.element(`#${gridElemId}`);

    if (gridElem) {
      const scope = this.getScope();

      const columnNames = columns.map((c) => c.name);
      let gridData = row.map((r) => {
        return columnNames.reduce((acc, colName, index) => {
          acc[colName] = r[index];
          return acc;
        }, {});
      });
      gridData.map((data) => {
        scope[gridElemId].data.push(data);
      });
    }
  }

  render(tableData) {
    const gridElemId = this.getGridElemId();
    let gridElem = document.getElementById(gridElemId);

    const config = this.config;
    const self = this; // for closure
    const scope = this.getScope();
    // set gridApi for this elem
    const gridApiId = this.getGridApiId();
    const gridOptions = this.createGridOptions(tableData, onRegisterApiCallback, config);

    const onRegisterApiCallback = (gridApi) => {
      scope[gridApiId] = gridApi;
      // should restore state before registering APIs

      // register callbacks for change evens
      // should persist `self.config` instead `config` (closure issue)
      gridApi.core.on.columnVisibilityChanged(scope, () => {
        self.persistConfigWithGridState(self.config);
      });
      gridApi.core.on.sortChanged(scope, () => {
        self.persistConfigWithGridState(self.config);
      });
      gridApi.colResizable.on.columnSizeChanged(scope, () => {
        self.persistConfigWithGridState(self.config);
      });

      gridApi.grid.renderContainers.body.adjustColumns = function adjustColumns(scrollLeft, scrollPercentage) {
        const self = this;

        const columnCache = self.visibleColumnCache;

        const canvasWidth = self.getCanvasWidth();
        const viewportWidth = self.getViewportWidth();

        const shouldLimitColumns =
          columnCache.length > self.grid.options.columnVirtualizationThreshold &&
          canvasWidth > viewportWidth;

        let colIndex;
        let newRange;
        if (shouldLimitColumns) {
          // Calculate the scroll percentage according to the scrollLeft location
          // if no percentage was provided
          if ((typeof(scrollPercentage) === 'undefined' || scrollPercentage === null) && scrollLeft) {
            scrollPercentage = scrollLeft / self.getHorizontalScrollLength();
          }

          // determine where column rendering should start by canvas width
          const availableCanvasWidth = canvasWidth - viewportWidth;
          const startPos = Math.max(availableCanvasWidth * scrollPercentage, 0);
          const stopPos = Math.min(startPos + viewportWidth, canvasWidth);

          const bounds = _.reduce(columnCache, function(acc, col, i) {
            const pos = acc[2];
            if (pos < stopPos) {
              const colWidth = col.drawnWidth ? col.drawnWidth : 0;
              // haven't stopped, see if we've started yet
              if (pos > startPos) {
                return [acc[0], i, pos + colWidth];
              } else {
                return [i, i, pos + colWidth];
              }
            } else {
              return acc;
            }
          }, [0, 0, 0]);

          const rangeStart = Math.max(0, bounds[0] - self.grid.options.excessColumns);
          const rangeEnd = Math.min(columnCache.length, bounds[1] + self.grid.options.excessColumns);

          colIndex = bounds[0];
          newRange = [rangeStart, rangeEnd];
        } else {
          colIndex = 0;
          newRange = [0, columnCache.length];
        }

        self.updateViewableColumnRange(newRange);

        self.prevColumnScrollIndex = colIndex;
      };
    };

    if (!gridElem) {
      // create, compile and append grid elem
      gridElem = angular.element(
        `<div id="${gridElemId}" ui-grid="${gridElemId}"
              ui-grid-empty-base-layer
              ui-grid-resize-columns
              ui-grid-save-state></div>`);

      gridElem.css('height', this.targetEl.height() - 10);
      gridElem = this._compile(gridElem)(scope);
      this.targetEl.append(gridElem);
      this.setDynamicGridOptions(gridOptions, config);
      this.addColumnMenus(gridOptions);
      scope[gridElemId] = gridOptions;
      gridOptions.onRegisterApi = onRegisterApiCallback;
    } else {
      scope[gridElemId] = gridOptions;
      this.setDynamicGridOptions(gridOptions, config);
      this.refreshGrid();
    }

    const columnDefs = this.getGridOptions().columnDefs;
    updateColumnTypeState(tableData.columns, config, columnDefs);
    // SHOULD restore grid state after columnDefs are updated
    this.restoreGridState(config.tableGridState);
  }

  restoreGridState(gridState) {
    if (!gridState) {
      return;
    }

    // should set isRestoring to avoid that changed* events are triggered while restoring
    this.isRestoring = true;
    const gridApi = this.getGridApi();

    // restore grid state when gridApi is available
    if (!gridApi) {
      setTimeout(() => this.restoreGridState(gridState), 100);
    } else {
      gridApi.saveState.restore(this.getScope(), gridState);
      this.isRestoring = false;
    }
  }

  destroy() {
  }

  getTransformation() {
    return this.passthrough;
  }

  getScope() {
    const scope = this.targetEl.scope();
    return scope;
  }

  getGridOptions() {
    const scope = this.getScope();
    const gridElemId = this.getGridElemId();
    return scope[gridElemId];
  }

  getGridApi() {
    const scope = this.targetEl.scope();
    const gridApiId = this.getGridApiId();
    return scope[gridApiId];
  }

  persistConfigImmediatelyWithGridState(config) {
    this.persistConfigWithGridState(config);
  }

  persistConfigWithGridState(config) {
    if (this.isRestoring) {
      return;
    }

    const gridApi = this.getGridApi();
    config.tableGridState = gridApi.saveState.save();
    this.emitConfig(config);
  }

  persistConfig(config) {
    this.emitConfig(config);
  }

  getSetting(chart) {
    const self = this; // for closure in scope
    const configObj = self.config;

    // emit config if it's updated in `render`
    if (configObj.initialized) {
      configObj.initialized = false;
      this.persistConfig(configObj); // should persist w/o state
    } else if (configObj.tableColumnTypeState &&
      configObj.tableColumnTypeState.updated) {
      configObj.tableColumnTypeState.updated = false;
      this.persistConfig(configObj); // should persist w/o state
    }

    return {
      template: SETTING_TEMPLATE,
      scope: {
        config: configObj,
        tableOptionSpecs: TABLE_OPTION_SPECS,
        isInputWidget: isInputWidget,
        isOptionWidget: isOptionWidget,
        isCheckboxWidget: isCheckboxWidget,
        isTextareaWidget: isTextareaWidget,
        isBtnGroupWidget: isBtnGroupWidget,
        tableOptionValueChanged: () => {
          self.persistConfigWithGridState(configObj);
        },
        saveTableOption: () => {
          self.persistConfigWithGridState(configObj);
        },
        resetTableOption: () => {
          resetTableOptionConfig(configObj);
          initializeTableConfig(configObj, TABLE_OPTION_SPECS);
          self.persistConfigWithGridState(configObj);
        },
        tableWidgetOnKeyDown: (event, optSpec) => {
          const code = event.keyCode || event.which;
          if (code === 13 && isInputWidget(optSpec)) {
            self.persistConfigWithGridState(configObj);
          } else if (code === 13 && event.shiftKey && isTextareaWidget(optSpec)) {
            self.persistConfigWithGridState(configObj);
          }

          event.stopPropagation(); /** avoid to conflict with paragraph shortcuts */
        },
      },
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/visualization/builtins/visualization-table.js